import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DirhamIconColored } from '../components/DirhamIcon';
import CurrencyDisplay, { CurrencyLabel } from '../components/CurrencyDisplay';
import { 
  Building2, 
  FileText, 
  Zap, 
  Droplets,
  Calculator,
  Save,
  Plus,
  Trash2,
  Edit3
} from 'lucide-react';

const FixedCommitments = () => {
  const { language } = useLanguage();
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [commitments, setCommitments] = useState({
    annual_rent: 0,
    annual_license: 0,
    other_annual: []
  });
  const [monthlyBills, setMonthlyBills] = useState([]);
  const [newBill, setNewBill] = useState({ name: '', amount: 0 });
  const [showAddBill, setShowAddBill] = useState(false);
  const [editingBill, setEditingBill] = useState(null);

  useEffect(() => {
    if (isAdmin()) {
      fetchCommitments();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCommitments = async () => {
    try {
      setLoading(true);
      const res = await api.get('/fixed-commitments');
      if (res.data) {
        setCommitments({
          annual_rent: res.data.annual_rent || 0,
          annual_license: res.data.annual_license || 0,
          other_annual: res.data.other_annual || []
        });
        setMonthlyBills(res.data.monthly_bills || []);
      }
    } catch (error) {
      console.log('No commitments found, using defaults');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCommitments = async () => {
    try {
      setSaving(true);
      await api.post('/fixed-commitments', {
        annual_rent: commitments.annual_rent,
        annual_license: commitments.annual_license,
        other_annual: commitments.other_annual,
        monthly_bills: monthlyBills
      });
      toast.success('تم حفظ الالتزامات بنجاح');
    } catch (error) {
      toast.error('فشل حفظ الالتزامات');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBill = () => {
    if (!newBill.name || newBill.amount <= 0) {
      toast.error('يرجى إدخال اسم ومبلغ الفاتورة');
      return;
    }
    setMonthlyBills([...monthlyBills, { ...newBill, id: Date.now().toString() }]);
    setNewBill({ name: '', amount: 0 });
    setShowAddBill(false);
  };

  const handleDeleteBill = (id) => {
    setMonthlyBills(monthlyBills.filter(b => b.id !== id));
  };

  const handleUpdateBill = (id, field, value) => {
    setMonthlyBills(monthlyBills.map(b => 
      b.id === id ? { ...b, [field]: value } : b
    ));
  };

  // Calculate monthly amounts
  const monthlyRent = commitments.annual_rent / 12;
  const monthlyLicense = commitments.annual_license / 12;
  const totalMonthlyBills = monthlyBills.reduce((sum, b) => sum + (parseFloat(b.amount) || 0), 0);
  const totalMonthlyCommitments = monthlyRent + monthlyLicense + totalMonthlyBills;

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">هذه الصفحة متاحة للمدير فقط</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="fixed-commitments-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Building2 className="w-8 h-8 text-primary" />
            الالتزامات الثابتة
          </h1>
          <p className="text-muted-foreground mt-1">
            إدارة المصروفات الثابتة للصالون (الإيجار، الرخصة، الفواتير)
          </p>
        </div>
        <Button
          onClick={handleSaveCommitments}
          disabled={saving}
          className="bg-primary text-primary-foreground gap-2"
        >
          <Save className="w-4 h-4" />
          {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
        </Button>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/20 rounded-xl p-6" data-testid="summary-card">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary" />
          ملخص الالتزامات الشهرية
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-black/20 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">الإيجار الشهري</p>
            <p className="text-xl font-bold text-primary"><CurrencyDisplay amount={monthlyRent} className="text-primary" /></p>
          </div>
          <div className="bg-black/20 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">حصة الرخصة الشهرية</p>
            <p className="text-xl font-bold text-primary"><CurrencyDisplay amount={monthlyLicense} className="text-primary" /></p>
          </div>
          <div className="bg-black/20 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">الفواتير الشهرية</p>
            <p className="text-xl font-bold text-primary"><CurrencyDisplay amount={totalMonthlyBills} className="text-primary" /></p>
          </div>
          <div className="bg-primary/30 rounded-lg p-4 border-2 border-primary">
            <p className="text-sm text-muted-foreground">إجمالي الالتزامات الشهرية</p>
            <p className="text-2xl font-bold text-primary"><CurrencyDisplay amount={totalMonthlyCommitments} className="text-primary" /></p>
          </div>
        </div>
      </div>

      {/* Annual Commitments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Annual Rent */}
        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="annual-rent-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h3 className="font-semibold">الإيجار السنوي</h3>
              <p className="text-sm text-muted-foreground">يُقسم على 12 شهر</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2"><CurrencyLabel label="المبلغ السنوي" /></label>
              <Input
                type="number"
                value={commitments.annual_rent}
                onChange={(e) => setCommitments({ ...commitments, annual_rent: parseFloat(e.target.value) || 0 })}
                className="bg-secondary/50"
                min="0"
              />
            </div>
            <div className="bg-secondary/30 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">الحصة الشهرية:</span>
                <span className="text-xl font-bold text-blue-500"><CurrencyDisplay amount={monthlyRent} className="text-blue-500" /></span>
              </div>
            </div>
          </div>
        </div>

        {/* Annual License */}
        <div className="bg-card border border-white/5 rounded-xl p-6" data-testid="annual-license-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-500/20 rounded-lg">
              <FileText className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <h3 className="font-semibold">رسوم الرخصة السنوية</h3>
              <p className="text-sm text-muted-foreground">تُقسم على 12 شهر</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2"><CurrencyLabel label="المبلغ السنوي" /></label>
              <Input
                type="number"
                value={commitments.annual_license}
                onChange={(e) => setCommitments({ ...commitments, annual_license: parseFloat(e.target.value) || 0 })}
                className="bg-secondary/50"
                min="0"
              />
            </div>
            <div className="bg-secondary/30 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">الحصة الشهرية:</span>
                <span className="text-xl font-bold text-purple-500"><CurrencyDisplay amount={monthlyLicense} className="text-purple-500" /></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Bills */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden" data-testid="monthly-bills-section">
        <div className="p-4 border-b border-white/5 flex justify-between items-center">
          <h3 className="font-semibold flex items-center gap-2">
            <DirhamIconColored className="w-5 h-5 text-primary" />
            الفواتير والمصروفات الشهرية
          </h3>
          <Button
            onClick={() => setShowAddBill(true)}
            size="sm"
            className="bg-primary text-primary-foreground gap-2"
          >
            <Plus className="w-4 h-4" />
            إضافة فاتورة
          </Button>
        </div>

        {/* Add Bill Form */}
        {showAddBill && (
          <div className="p-4 bg-secondary/20 border-b border-white/5">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-2">اسم الفاتورة</label>
                <Input
                  value={newBill.name}
                  onChange={(e) => setNewBill({ ...newBill, name: e.target.value })}
                  placeholder="مثال: فاتورة الكهرباء"
                  className="bg-secondary/50"
                />
              </div>
              <div className="w-40">
                <label className="block text-sm font-medium mb-2"><CurrencyLabel label="المبلغ" /></label>
                <Input
                  type="number"
                  value={newBill.amount}
                  onChange={(e) => setNewBill({ ...newBill, amount: parseFloat(e.target.value) || 0 })}
                  className="bg-secondary/50"
                  min="0"
                />
              </div>
              <Button onClick={handleAddBill} className="bg-green-500 hover:bg-green-600">
                <Plus className="w-4 h-4" />
              </Button>
              <Button onClick={() => setShowAddBill(false)} variant="outline">
                إلغاء
              </Button>
            </div>
          </div>
        )}

        {/* Bills List */}
        <div className="p-4">
          {monthlyBills.length === 0 ? (
            <div className="text-center py-8">
              <DirhamIconColored className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">لا توجد فواتير شهرية مضافة</p>
              <p className="text-sm text-muted-foreground">أضف فواتير مثل الكهرباء والماء والإنترنت</p>
            </div>
          ) : (
            <div className="space-y-3">
              {monthlyBills.map((bill) => (
                <div 
                  key={bill.id} 
                  className="flex items-center gap-4 p-4 bg-secondary/20 rounded-lg"
                >
                  <div className="p-2 bg-primary/20 rounded-lg">
                    {bill.name?.includes('كهرباء') ? <Zap className="w-5 h-5 text-yellow-500" /> :
                     bill.name?.includes('ماء') ? <Droplets className="w-5 h-5 text-blue-500" /> :
                     <DirhamIconColored className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="flex-1">
                    {editingBill === bill.id ? (
                      <Input
                        value={bill.name}
                        onChange={(e) => handleUpdateBill(bill.id, 'name', e.target.value)}
                        className="bg-secondary/50"
                      />
                    ) : (
                      <span className="font-medium">{bill.name}</span>
                    )}
                  </div>
                  <div className="w-32">
                    {editingBill === bill.id ? (
                      <Input
                        type="number"
                        value={bill.amount}
                        onChange={(e) => handleUpdateBill(bill.id, 'amount', parseFloat(e.target.value) || 0)}
                        className="bg-secondary/50"
                        min="0"
                      />
                    ) : (
                      <span className="font-bold text-primary"><CurrencyDisplay amount={parseFloat(bill.amount)} className="text-primary" /></span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingBill(editingBill === bill.id ? null : bill.id)}
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-400"
                      onClick={() => handleDeleteBill(bill.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Total */}
        {monthlyBills.length > 0 && (
          <div className="p-4 border-t border-white/5 bg-secondary/10">
            <div className="flex justify-between items-center">
              <span className="font-semibold">إجمالي الفواتير الشهرية:</span>
              <span className="text-2xl font-bold text-primary"><CurrencyDisplay amount={totalMonthlyBills} className="text-primary" /></span>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <h4 className="font-semibold text-blue-400 mb-2">كيف يتم استخدام هذه البيانات؟</h4>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li>يتم تقسيم الالتزامات الشهرية بالتساوي على جميع الحلاقين</li>
          <li>تُخصم حصة كل حلاق من إيراداته قبل حساب الراتب</li>
          <li>يُستخدم في التقارير الشهرية لحساب صافي ربح الصالون</li>
          <li>إذا كان راتب الحلاق أقل من <CurrencyDisplay amount={1800} showIcon={false} />، يُرفع إلى 1800 والفرق يُسجل كمصروف عليه</li>
        </ul>
      </div>
    </div>
  );
};

export default FixedCommitments;
